import { isAffidavitDisabled } from 'lib/affidavits';
import { Product } from 'lib/enums';
import { getActiveDiscountConfigForCustomer } from 'lib/notice/discounts';
import { DBPricingObj, createDBPricingObjectFromData } from 'lib/pricing';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { getOrThrow } from 'lib/utils/refs';
import { EPlacement } from 'redux/placement';
import { getBooleanFlag } from './flags';

/**
 * Generate a DBPricingObj based on the data in the placement flow, if available.
 */
export const createDbPricingObjFromPlacement = async (
  placement: EPlacement,
  productType: Product
): Promise<DBPricingObj> => {
  if (!placement.rate) {
    throw new Error('Cannot price before placement.rate is set');
  }

  if (!placement.newspaper) {
    throw new Error('Cannot price before placement.newspaper is set');
  }

  if (!placement.publicationDates?.length) {
    throw new Error('Cannot price before placement.publicationDates is set');
  }

  if (typeof placement.noticeType !== 'number') {
    throw new Error('Cannot price before placement.noticeType is set');
  }

  const rateSnap = await getOrThrow(placement.rate);
  const rate = rateSnap.data();

  const newspaperSnap = await getOrThrow(placement.newspaper);
  const newspaper = newspaperSnap.data();

  const customer = await placement.customer?.get();
  const customerOrg = await placement.customerOrganization?.get();
  const discountConfig = getActiveDiscountConfigForCustomer(
    customer,
    customerOrg,
    new Date()
  );
  const requiresAffidavit = !isAffidavitDisabled(placement, newspaperSnap);

  const enablePeriodBasedOrderPricing = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_PERIOD_BASED_ORDER_PRICING
  );

  const enableFeeSplitsForOrders = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_FEE_SPLITS_FOR_ORDERS
  );

  const enableNewOklahomaRate = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_NEW_OKLAHOMA_RATE
  );

  return createDBPricingObjectFromData(
    productType,
    {
      noticeType: placement.noticeType,
      publicationDates: placement.publicationDates,
      columns: placement.columns ?? 1
    },
    newspaper,
    rate,
    placement.displayParams ?? undefined,
    {
      settings: undefined,
      mail: placement.mail ?? [],
      requiresAffidavit
    },
    discountConfig,
    {
      enablePeriodBasedOrderPricing,
      enableFeeSplitsForOrders,
      enableNewOklahomaRate
    }
  );
};
